import React from 'react'
import { makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200
  },
  modal: {
    width: '85vw',
    maxWidth: '1100px',
    height: '80vh',
    backgroundColor: 'white',
    borderRadius: 'var(--border-radius-lg)',
    border: '3px solid var(--color-neutral-300)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-lg)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 20px',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    flexShrink: 0
  },
  methodBadge: {
    padding: '2px 8px',
    borderRadius: '3px',
    fontSize: '12px',
    fontWeight: 700,
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-700)',
    fontFamily: 'var(--font-mono, monospace)',
    flexShrink: 0
  },
  title: {
    flex: 1,
    fontFamily: 'var(--font-mono, monospace)',
    fontWeight: 600,
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-800)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  statusBadge: {
    fontSize: '13px',
    fontWeight: 600
  },
  mockedTag: {
    padding: '0 4px',
    borderRadius: '2px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: 'var(--color-primary-050)',
    color: 'var(--color-primary-600)'
  },
  duration: {
    fontSize: '12px',
    color: 'var(--color-neutral-400)'
  },
  closeBtn: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'white',
    cursor: 'pointer',
    fontSize: '16px',
    color: 'var(--color-neutral-500)',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'var(--color-neutral-100)',
      color: 'var(--color-neutral-800)'
    }
  },
  body: {
    flex: 1,
    display: 'flex',
    minHeight: 0,
    overflow: 'hidden'
  },
  pane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
    '&:first-child': {
      borderRight: '1px solid var(--color-neutral-200)'
    }
  },
  paneHeader: {
    padding: '8px 16px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--color-neutral-500)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)',
    flexShrink: 0
  },
  panePre: {
    flex: 1,
    margin: 0,
    padding: '12px 16px',
    fontSize: '12px',
    fontFamily: 'var(--font-mono, monospace)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    color: 'var(--color-neutral-700)',
    lineHeight: 1.5,
    overflow: 'auto'
  },
  paneEmpty: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 'var(--text-size-sm)',
    fontStyle: 'italic'
  }
}))

function SharedRequestDetailModal ({ request, onClose }) {
  const classes = useStyles()
  if (!request) return null

  return (
    <div className={classes.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className={classes.modal}>
        <div className={classes.header}>
          <span className={classes.methodBadge}>{request.method}</span>
          <span className={classes.title}>{request.url}</span>
          <span
            className={classes.statusBadge}
            style={{ color: request.status < 300 ? 'var(--color-success-600)' : 'var(--color-danger-600)' }}
          >
            {request.status}
          </span>
          {request.mocked && <span className={classes.mockedTag}>mock</span>}
          {request.durationMs != null && (
            <span className={classes.duration}>{request.durationMs}ms</span>
          )}
          <button className={classes.closeBtn} onClick={onClose} aria-label='Close request detail'>&times;</button>
        </div>
        <div className={classes.body}>
          <div className={classes.pane}>
            <div className={classes.paneHeader}>Request Body</div>
            {request.requestBody
              ? <pre className={classes.panePre}>{JSON.stringify(request.requestBody, null, 2)}</pre>
              : <div className={classes.paneEmpty}>No request body</div>}
          </div>
          <div className={classes.pane}>
            <div className={classes.paneHeader}>Response Body</div>
            {request.responseBody
              ? <pre className={classes.panePre}>{JSON.stringify(request.responseBody, null, 2)}</pre>
              : <div className={classes.paneEmpty}>No response body</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

SharedRequestDetailModal.propTypes = {
  request: PropTypes.object,
  onClose: PropTypes.func.isRequired
}

export default SharedRequestDetailModal
